import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IAxedSeries, ISeries } from '../pieces/series/index';
import { RoundingHelper, NumberFormatHelper, ILogger, LoggerFactory } from '../../helpers/helpers';
import { VerticalAxis, TimeAxis } from '../pieces/axes/index';
import { ChartBase } from './ChartBase';
import { IndividualAxes, Crosshair, Zoom } from '../pieces/index';
import { IChartOptions } from '../../models/IChartOptions';
import { ZoomMode } from '../../models/Mode';
import { IChart } from '../interfaces/IChart';
import { SeriesManager } from '../managers/SeriesManager';
import { SvgManager } from '../SvgManager';
import { OwnAxes } from '../pieces/ownAxes';
import { INewSeries } from '../managers/INewSeries';


export interface ITimeChart extends IChart {
    isTitleVisible(value?: boolean): boolean;
    isLegendVisible(value?: boolean): boolean;
    isHorizontalGridlinesVisible(value?: boolean): void;
    removeSeries(series): void;
}

export class TimeChartBase extends ChartBase {
    private _axesGroup: d3.Selection<any>;
    private _crosshair: Crosshair<Date>;
    private _crosshairGroup: d3.Selection<any>;
    private _data = [];
    private _horizontalGridlinesGroup: d3.Selection<any>;
    private _horizontalLines: any[];
    private _horizontalLinesGroup: d3.Selection<any>;
    private _individualAxes: IndividualAxes;
    private _individualScales: IndividualAxis[];
    private _isHorizontalGridlinesVisible: boolean;
    private _isXAxisVisible: boolean;
    private _isYAxisDefaultFormat: boolean;
    private _isYAxisVisible: boolean;
    private _loggerBase: ILogger;
    private _roundingHelper: RoundingHelper;
    private _series: IAxedSeries[];
    private _seriesManager: SeriesManager;
    private _ticksCount: number;
    private _xAxis: TimeAxis;
    private _yAxis: VerticalAxis;
    private _yAxisFormat: string;
    private _yMax: number;
    private _yMin: number;
    private _zoom: Zoom;
    private _zoomDispatch;

    constructor(options: IChartOptions, svgManager: SvgManager, classed: string) {
        //TODO handle axis with time the same way as others
        super(options, svgManager, classed, false);
        this.create(this.plotAreaGroup());
        this._roundingHelper = new RoundingHelper();
    }

    create(container: d3.Selection<any>) {
        this._ticksCount = 6;
        this._yAxisFormat = '.2s';
        this._series = [];
        this._horizontalLines = [];
        this._individualScales = [];
        this._isYAxisDefaultFormat = true;
        this._loggerBase = LoggerFactory.getLogger('WideTech.Charting.timeChartBase', false, 0);
        this._zoomDispatch = d3.dispatch('zoom', 'selection');

        this.init(container);
        this.resizeFunction(this.applyResize());

        this._seriesManager = new SeriesManager(_, d3);
        this._seriesManager.container(this.seriesGroup());

        this._seriesManager.postAdd(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.addSeries(series);
            }
        });
        this._seriesManager.postUpdate(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.updateSeries(series);
            }
        });
        this._seriesManager.postRemove(series => {
            if (!series.isNotDisplayedData) {
                this._crosshair.removeSeries(series);
            }
        });

        this._individualAxes = new IndividualAxes(this._axesGroup);
        this.applyResize()();
    }

    private init(container: d3.Selection<any>) {
        this.initAxes(container);
        this.initCrosshair(container);

        this._horizontalGridlinesGroup = container
            .insert('g', '.series-group')
            .classed('horizontal-gridlines', true);

        container
            .insert('g', '.series-group')
            .classed('stacked-series', true);

        this._horizontalLinesGroup = container
            .insert('g', '.crosshair-group')
            .classed('horizontal-lines', true);
        this._zoom = new Zoom(container, this._xAxis, this._yAxis);
        this._zoom.dispatch().on('zoom', this.onZoom());
        this._zoom.dispatch().on('unzoom', this.onUnzoom());
        this._zoom.dispatch().on('selection', d => this.dispatch().selection(d));
    }

    private initAxes(container) {
        this._axesGroup = container
            .append('g')
            .classed('axes-group', true);
        const beginDate = moment().add(-7, 'day');
        const endDate = moment();
        this._xAxis = new TimeAxis(
            this.plotAreaWidth(),
            beginDate,
            endDate,
            this._ticksCount,
            this._axesGroup,
            this.isYAxisVisible()
        );

        this._yAxis = new VerticalAxis(
            this.plotAreaHeight(),
            0,
            1,
            this._ticksCount,
            this._axesGroup
        );
        this.plotArea().resizeFunction()(this.plotAreaWidth(), Math.max(this.plotAreaHeight() - 20, 0));
    }

    private initCrosshair(container) {
        this._crosshairGroup = container
            .append('g')
            .classed('crosshair-group', true);
        this._crosshair = new Crosshair<Date>(this.plotAreaWidth(),
            this.plotAreaHeight(),
            this._crosshairGroup);
    }

    private onZoom(): (data) => void {
        const self = this;
        return data => {
            self.updateXAxis(data.beginDate, data.endDate);
            self._individualScales.forEach(s => {
                s.unzoomedData.push({
                    yMin: s.scale.domain()[0],
                    yMax: s.scale.domain()[1]
                });
                const iYMin = s.scale.invert(data.maxPos);
                const iYMax = s.scale.invert(data.minPos);
                s.scale.domain([iYMin, iYMax]);
                if (!s.unzoomedData) {
                    s.unzoomedData = [];
                }
            });
            self.updateInividualSeries(data.beginDate, data.endDate);
            self.display(data.min, data.max);
            self._zoomDispatch.zoom();
        };
    }

    private onUnzoom(): (data) => void {
        const self = this;
        return data => {
            self.updateXAxis(data.beginDate, data.endDate);
            self._individualScales.forEach(s => {
                const unzoomed = s.unzoomedData.pop();
                const iYMin = unzoomed.yMin;
                const iYMax = unzoomed.yMax;
                s.scale.domain([iYMin, iYMax]);
            });
            self.updateInividualSeries(data.beginDate, data.endDate);
            self.display(data.min, data.max);
        };
    }

    private zoom(self: this) {
        return () => {
            self._xAxis.apply();
            self._series.forEach((d: any) => {
                try {
                    d.changeXScale(self._xAxis.scale());
                } catch (e) {
                    self._loggerBase.error(e);
                    self._loggerBase.error(`Object: ${JSON.stringify(d)}`);
                }
            });
        };
    }

    private updateInividualSeries(beginDate, endDate) {
        this._series
            .filter((s: any) => s.hasOwnAxis && s.hasOwnAxis())
            .forEach((s: any) => {
                const scale = _.find(this._individualScales, is => is.id === 'axis' + s.identifier()).scale;
                const seriesData = this._data
                    .filter(d => s.selector()(d) === s.identifier() &&
                        moment(d.timeStamp) >= moment(beginDate) &&
                        moment(d.TimeStamp) >= moment(endDate));

                s.update(seriesData, this._xAxis.scale(), scale);
            });
    }

    private updateHorizontalGridlines() {
        if (this.isHorizontalGridlinesVisible()) {
            const gridLines = d3.svg.axis()
                .scale(this._yAxis.scale())
                .ticks(this._ticksCount)
                .orient('left')
                .tickSize(-this.plotAreaWidth(), 0)
                .tickFormat(() => '');
            const selection = this._horizontalGridlinesGroup;
            gridLines(selection);
            selection
                .selectAll('line')
                .classed('gridline', true);
        }
    }

    private updateXAxis(beginDate, endDate) {
        this._xAxis.update(beginDate, endDate);
        this._crosshair.scale(this._xAxis.scale());
        this.updateHorizontalGridlines();
    }

    private updateYAxis(min, max) {
        const self = this;
        let fmt = self.yAxisFormat();
        const ticksCount = 6;
        if (self._isYAxisDefaultFormat || self.yAxisFormat() === 'f') {
            fmt = NumberFormatHelper.getFormatString(min, max);
        }
        this._yAxis.tickFormat((d) => {
            return d3.format(fmt)(d);
        });
        this._yAxis.tickCount(ticksCount);
        this._yAxis.update(min, max);
        this.updateHorizontalGridlines();
    }

    private applyResize() {
        const self = this;
        return () => {
            self._loggerBase.debug('Apply resize');
            const plotAreaHeight = this.plotAreaHeight();
            const plotAreaWidth = this.plotAreaWidth();
            self.resizeAxes(self, plotAreaWidth, plotAreaHeight);
            self._series.forEach(s => {
                s.resize(self._xAxis.scale(), self._yAxis.scale());
            });
            self._individualAxes.resize(plotAreaWidth, plotAreaHeight);
            self.updateHorizontalGridlines();
            self.drawHorizontalLines();
            self.title()
                .resize(plotAreaWidth + 2 * self.options().translateX,
                    this.height());

            self._crosshair.resize(plotAreaWidth, plotAreaHeight);
            self.translate(plotAreaHeight);
        };
    }

    private resizeAxes(self: this, width, height) {
        self.executeHandled(() => {
            self._xAxis.resize(width);
            self._yAxis.resize(height);
        });
    }

    private translate(height) {
        try {
            this._xAxis.translate(0, height);
        } catch (e) {
            this._loggerBase.error(e);
        }
    }

    private display(min, max) {
        this.updateYAxis(min, max);
        if (this._data) {
            let i = 1;
            this.updateIndividualAxes();
            this._series.forEach((s: any) => {
                let scale = this._yAxis.scale();
                if (s.hasOwnAxis && s.hasOwnAxis()) {
                    const obj = this.manageIndividualScale(s, i);
                    scale = obj.scale;
                    i++;
                }
                const current = this._data.filter(d => s.selector()(d) === s.identifier());
                s.update(current, this._xAxis.scale(), scale);
            });
        }
        this.drawHorizontalLines();
    }

    private manageIndividualScale(s: any, i: number) {
        let obj = _.find(this._individualScales, individualScale => individualScale.id === `axis${s.identifier()}`);
        if (!obj) {
            obj = this.createIndividualAxis(this._roundingHelper, i, s);
            this._individualScales.push(obj);
        }
        let seriesMin = s.min();
        let seriesMax = s.max();
        const round = this._roundingHelper.round(seriesMin, seriesMax);
        const yMin = s.yMin();
        if (!yMin && yMin !== 0) {
            seriesMin = round.min;
        }
        const yMax = s.yMax();
        if (!yMax && yMax !== 0) {
            seriesMax = round.max;
        }
        obj.scale.domain([seriesMin, seriesMax]);
        return obj;
    }

    private updateIndividualAxes() {
        const dic = {};
        this._individualScales.forEach(s => {
            dic[s.id] = s.scale;
        });
        const ownAxes = OwnAxes(this._series, dic);
        this._individualAxes.update(ownAxes, this.plotAreaHeight(), this.plotAreaWidth());
    }

    private createIndividualAxis(helper: RoundingHelper, i: number, s: IAxedSeries): IndividualAxis {
        const rounded = helper.round(s.min(), s.max());
        const scale = d3.scale.linear<any, any>();
        scale.domain([rounded.min, rounded.max]);
        scale.range(this._yAxis.scale().range());
        const id = `axis${s.identifier()}`;
        return { scale: <any>scale, id: id, unzoomedData: [], series: s };
    }

    private drawHorizontalLines() {
        const self = this;
        const dataSelection = this._horizontalLinesGroup
            .selectAll('line')
            .data(this._horizontalLines);
        dataSelection.enter()
            .append('line')
            .classed('fixed-line', true);
        dataSelection.attr({
            'x1': 0,
            'x2': self.plotAreaWidth(),
            'y1': (d) => self._yAxis.scale()(d.value),
            'y2': (d) => self._yAxis.scale()(d.value)
        })
            .style({
                'stroke': (d) => d.color,
                'stroke-width': 1,
                'stroke-dasharray': '5,2'
            });
        dataSelection.exit().remove();
    }

    isHorizontalGridlinesVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isHorizontalGridlinesVisible = value;
            if (value) {
                this.updateHorizontalGridlines();
                this._horizontalGridlinesGroup.style('visibility', 'visible');
            } else {
                this._horizontalGridlinesGroup.style('visibility', 'collapse');
            }
        }
        return this._isHorizontalGridlinesVisible;
    }

    isXAxisVisible(value?: boolean): boolean {
        if (value !== undefined) {
            this._isXAxisVisible = value;
            this.refreshAxisX(this);
        }
        return this._isXAxisVisible;
    }

    isYAxisVisible(value?: boolean): boolean {
        if (value !== undefined) {
            this._isYAxisVisible = value;
            this.refreshAxisY(this);
        }
        return this._isYAxisVisible;
    }

    yAxisFormat(value?: string): string {
        if (value) {
            this._yAxisFormat = value;
            this._isYAxisDefaultFormat = false;
        }
        return this._yAxisFormat;
    }

    yMin(value?) {
        if (value !== undefined) {
            this._yMin = value;
            this.refreshAxisY(this);
        }
        return this._yMin;
    }

    yMax(value?) {
        if (value !== undefined) {
            this._yMax = value;
            this.refreshAxisY(this);
        }
        return this._yMax;
    }

    xMin(value?: Date): Date {
        const min = this._xAxis.min(value);
        if (arguments.length > 0) {
            this.refreshAxisY(this);
        }
        return min;
    }

    xMax(value?: Date): Date {
        const max = this._xAxis.max(value);
        if (arguments.length > 0) {
            this.refreshAxisY(this);
        }
        return max;
    }

    xRange(value?: [Date, Date]): [Date, Date] {
        if (!!value && arguments.length > 0) {
            const min = this._xAxis.min(value[0]);
            const max = this._xAxis.max(value[1]);
            this.refreshAxisY(this);
            return [min, max];
        } else {
            return [this._xAxis.min(), this._xAxis.max()];
        }
    }
    
    private _localRatio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            super.ratio(value);
            this._xAxis.ratio(value);
            this._yAxis.ratio(value);
            this._localRatio = value;
            return this;
        } else {
            return this._localRatio;
        }
    }

    series(value?: INewSeries[]): ISeries[] {
        if (value) {
            this._seriesManager.updateSeries(value);
            this._series = this._seriesManager.series() as any;
            this.refreshAxisY(this);
            this.legend().series(this._series);
        }
        return this._seriesManager.series();
    }

    clearSeries() {
        this._seriesManager.updateSeries([]);
    }

    refreshAxisX(self: this) {
        // TODO ?
    }

    refreshAxisY(self: this) {
        try {
            const { min, max } = this.computeYMinMax(self);
            if (self.yAxis()) {
                const range = self.yAxis().scale().range();
                if (min !== range[0] || max !== range[1]) {
                    self.display(min, max);
                }
            } else {
                self.display(min, max);
            }
        } catch (e) {
            this._loggerBase.error(e);
        }
    }

    private computeYMinMax(self: this) {
        const yMin = self.yMin();
        const yMax = self.yMax();
        const hasYMin = yMin !== null && yMin !== undefined;
        const hasYMax = yMax !== null && yMax !== undefined;
        let min = 0;
        let max = 1;
        if (!hasYMin || !hasYMax) {
            const helper = new RoundingHelper();
            const seriesMin = self._seriesManager.min();
            if (seriesMin) {
                min = seriesMin;
            }
            const seriesMax = self._seriesManager.max();
            if (seriesMax) {
                max = seriesMax;
            }
            min = hasYMin ? yMin : min;
            max = hasYMax ? yMax : max;
            const rounded = helper.round(min, max);
            min = rounded.min;
            max = rounded.max;
        } else {
            min = yMin;
            max = yMax;
        }
        return { min, max };
    }

    update(data: any[]) {
        const self = this;
        this._data = data;
        this.executeHandled(() => {
            if (data) {
                let beginDate: Date;
                if (!self._xAxis.min()) {
                    beginDate = <Date>d3.min(data, (d: any) => new Date(<string>d.timeStamp));
                } else {
                    beginDate = self._xAxis.min();
                }
                let endDate: Date;
                if (!self._xAxis.max()) {
                    endDate = <Date>d3.max(data, (d: any) => new Date(<string>d.timeStamp));
                } else {
                    endDate = self._xAxis.max();
                }
                self.updateXAxis(beginDate, endDate);
                self._seriesManager.data(data);
                self.refreshAxisY(self);
                self._crosshair.update(data);
            }
        });
    }

    horizontalLines(value?: any[]): any[] {
        if (value) {
            this._horizontalLines = value;
            this.drawHorizontalLines();
        }
        return this._horizontalLines;
    }

    /*Zoom ---------------------------------------------------------- */
    resetZoom() {
        if (this._zoom) {
            this._zoom.resetZoom(this.group());
        }
    }

    resetSelection() {
        if (this._zoom) {
            this._zoom.resetSelection(this.group());
        }
    }

    isZoomed() {
        return !!this._zoom && this._zoom.isZoomed();
    }

    pan() {
        if (this._zoom) {
            this._zoom.pan(this.group());
            this.resetMode();
        }
    }

    select() {
        if (this._zoom) {
            this._zoom.select(this.group());
            this.resetMode();
        }
    }

    mode() {
        return this._zoom.mode();
    }

    dispatch() {
        return this._zoomDispatch;
    }
    /*------------------------------------------------------------ */

    data() {
        return this._data;
    }

    updateCrosshair(date: Date) {
        this._crosshair.setDate(date);
    }

    onCrosshair(callback) {
        this._crosshair.onCrosshair(callback);
    }

    private resetMode() {
        const mode = this._zoom.mode();
        if (mode === ZoomMode.Zoom) {
            this.configureCrosshair(false, false);
        } else if (mode === ZoomMode.Pan) {
            this.configureCrosshair(true, false);
        } else if (mode === ZoomMode.Selection) {
            this.configureCrosshair(false, true);
        }
    }

    private configureCrosshair(isPan: boolean, isSelection: boolean) {
        const self = this;

        this.group()
            .selectAll('.crosshair-rectangle')
            .classed('pan', isPan)
            .classed('selection', isSelection);

        const panMethod = isPan
            ? () => self.zoom(self)()
            : () => { /*nothing*/ };
        this._zoom.dispatch().on('pan', panMethod);
    }
}

class IndividualAxis {
    scale: d3.scale.Linear<any, any>;
    id: string;
    unzoomedData: any[];
    series: any;
}